<!-- 异常车辆预警 -->
<template>
  <div class="vehicle-alarm-page">
    <section class="filter-panel">
      <el-form :model="queryForm" size="small" label-width="76px" class="filter-form">
        <el-form-item label="统计时间">
          <el-date-picker
            v-model="dateRange"
            type="datetimerange"
            value-format="yyyy-MM-dd HH:mm:ss"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            :default-time="['00:00:00', '23:59:59']"
            unlink-panels
            clearable
          />
        </el-form-item>
        <el-form-item label="客户">
          <el-select
            v-model="queryForm.custNo"
            filterable
            remote
            clearable
            reserve-keyword
            :remote-method="loadCustomerOptions"
            :loading="customerLoading"
            placeholder="全部客户"
          >
            <el-option
              v-for="item in customerOptions"
              :key="item.id"
              :label="item.deptName"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="煤种">
          <el-select
            v-model="queryForm.cargoNo"
            filterable
            remote
            clearable
            reserve-keyword
            :remote-method="loadCargoOptions"
            :loading="cargoLoading"
            placeholder="全部煤种"
          >
            <el-option
              v-for="item in cargoOptions"
              :key="item.varNo"
              :label="item.varName"
              :value="item.varNo"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="车牌号">
          <el-input v-model.trim="queryForm.trainNumber" clearable placeholder="请输入车牌号" />
        </el-form-item>
        <el-form-item label="计划号">
          <el-input v-model.trim="queryForm.planNo" clearable placeholder="请输入计划号" />
        </el-form-item>
        <el-form-item label="车辆状态">
          <el-select v-model="queryForm.vehicleStatus" clearable placeholder="全部状态">
            <el-option
              v-for="item in vehicleStatusOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="超时阈值">
          <el-input-number
            v-model="queryForm.timeoutHours"
            :min="0.5"
            :step="0.5"
            :precision="1"
            controls-position="right"
          />
        </el-form-item>
        <el-form-item class="filter-actions">
          <el-button type="primary" icon="el-icon-search" :loading="queryLoading" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
          <el-button icon="el-icon-download" :loading="exportLoading" @click="handleExport">导出</el-button>
        </el-form-item>
      </el-form>
      <div class="quick-range">
        <el-button
          v-for="item in quickRanges"
          :key="item.value"
          size="mini"
          :type="activeRange === item.value ? 'primary' : 'default'"
          plain
          @click="setQuickRange(item.value)"
        >
          {{ item.label }}
        </el-button>
      </div>
    </section>

    <el-alert
      v-if="summaryError"
      class="state-alert"
      :title="summaryError"
      type="error"
      show-icon
      :closable="false"
    />

    <section v-loading="summaryLoading" class="summary-grid">
      <div v-for="card in summaryCards" :key="card.key" :class="['metric-card', card.accent]">
        <div class="metric-head">
          <span>{{ card.label }}</span>
          <span>{{ card.caption }}</span>
        </div>
        <div class="metric-value">
          {{ card.value }}<small>{{ card.unit }}</small>
        </div>
        <div class="metric-foot">{{ card.sub }}</div>
      </div>
    </section>

    <section class="analysis-grid">
      <div v-loading="summaryLoading" class="panel">
        <div class="panel-head">
          <span>客户超时 TOP5</span>
          <span>按超时车辆数排序</span>
        </div>
        <div ref="customerChart" class="chart-box" />
      </div>
      <div v-loading="summaryLoading" class="panel">
        <div class="panel-head">
          <span>超时时长分布</span>
          <span>按超时区间统计车辆数</span>
        </div>
        <div ref="durationChart" class="chart-box" />
      </div>
    </section>

    <section class="table-panel">
      <div class="panel-head table-head">
        <span>超时明细列表</span>
        <span>固定汽运出库，按进门时间筛选</span>
      </div>
      <el-alert
        v-if="tableError"
        class="state-alert table-error"
        :title="tableError"
        type="error"
        show-icon
        :closable="false"
      />
      <el-table
        v-loading="tableLoading"
        :data="records"
        border
        stripe
        size="small"
        empty-text="当前筛选条件下暂无超时车辆"
        style="width: 100%"
      >
        <el-table-column prop="trainNumber" label="车牌号" width="110" fixed show-overflow-tooltip />
        <el-table-column prop="planNo" label="计划号" width="150" show-overflow-tooltip />
        <el-table-column label="客户" min-width="150" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.custName || row.custNo || "--" }}</template>
        </el-table-column>
        <el-table-column label="煤种" min-width="130" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.cargoName || row.cargoNo || "--" }}</template>
        </el-table-column>
        <el-table-column label="车辆状态" width="92" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="vehicleStatusTag(row.vehicleStatus)" size="small">
              {{ vehicleStatusText(row.vehicleStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="driverName" label="司机" width="100" show-overflow-tooltip />
        <el-table-column prop="phoneNo" label="手机号" width="120" show-overflow-tooltip />
        <el-table-column prop="ingateTime" label="进门时间" width="155" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ formatDateTime(row.ingateTime) }}</template>
        </el-table-column>
        <el-table-column prop="emptyTime" label="空磅时间" width="155" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ formatDateTime(row.emptyTime) }}</template>
        </el-table-column>
        <el-table-column prop="heavyTime" label="重磅时间" width="155" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ formatDateTime(row.heavyTime) }}</template>
        </el-table-column>
        <el-table-column prop="outgateTime" label="出门时间" width="155" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ formatDateTime(row.outgateTime) }}</template>
        </el-table-column>
        <el-table-column label="净重(吨)" width="96" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.netWeight) }}</template>
        </el-table-column>
        <el-table-column label="在厂时长" width="115" align="right">
          <template slot-scope="{ row }">{{ formatDuration(row.staySeconds || row.plantStaySeconds) }}</template>
        </el-table-column>
        <el-table-column label="超时时长" width="115" align="right">
          <template slot-scope="{ row }">
            <span class="overtime-text">{{ formatDuration(row.overtimeSeconds) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="alarmReason" label="报警原因" min-width="160" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.alarmReason || "在厂超时" }}</template>
        </el-table-column>
      </el-table>
      <div class="pagination-bar">
        <span>共 {{ page.total }} 条</span>
        <el-pagination
          background
          layout="sizes, prev, pager, next, jumper"
          :current-page="page.currentPage"
          :page-size="page.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="page.total"
          @current-change="handleCurrentChange"
          @size-change="handleSizeChange"
        />
      </div>
    </section>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { downloadXls } from '@/util/util'
import {
  exportExcel,
  fetchCargoOptions,
  fetchCustomerOptions,
  fetchPage,
  fetchSummary
} from './api'

const DEFAULT_TIMEOUT_HOURS = 4
const EXPORT_MAX_ROWS = 10000

export default {
  name: 'VehicleAlarm',
  data() {
    return {
      queryForm: this.defaultQueryForm(),
      dateRange: [],
      activeRange: 'seven',
      quickRanges: [
        { label: '今日', value: 'today' },
        { label: '近7日', value: 'seven' },
        { label: '本月', value: 'month' }
      ],
      vehicleStatusOptions: [
        { label: '未开始', value: 0 },
        { label: '进行中', value: 1 },
        { label: '已完成', value: 2 }
      ],
      customerOptions: [],
      cargoOptions: [],
      customerLoading: false,
      cargoLoading: false,
      summaryLoading: false,
      tableLoading: false,
      exportLoading: false,
      summaryError: '',
      tableError: '',
      summaryData: {},
      summaryCards: [],
      customerTop: [],
      durationBuckets: [],
      records: [],
      page: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      charts: {
        customer: null,
        duration: null
      }
    }
  },
  computed: {
    queryLoading() {
      return this.summaryLoading || this.tableLoading
    }
  },
  mounted() {
    this.setQuickRange('seven', false)
    this.loadCustomerOptions('')
    this.loadCargoOptions('')
    this.loadData()
    window.addEventListener('resize', this.resizeCharts)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCharts)
    this.disposeCharts()
  },
  methods: {
    defaultQueryForm() {
      return {
        custNo: '',
        cargoNo: '',
        trainNumber: '',
        planNo: '',
        vehicleStatus: '',
        timeoutHours: DEFAULT_TIMEOUT_HOURS
      }
    },
    loadData() {
      this.loadSummary()
      this.loadTable()
    },
    async loadSummary() {
      this.summaryLoading = true
      this.summaryError = ''
      try {
        const data = this.unwrapResponse(await fetchSummary(this.buildQueryParams())) || {}
        this.summaryData = data
        this.applySummary(data)
      } catch (e) {
        console.warn(e)
        this.summaryData = {}
        this.applySummary({})
        this.summaryError = '看板数据加载失败，请稍后重试'
      } finally {
        this.summaryLoading = false
        this.$nextTick(() => this.renderCharts())
      }
    },
    async loadTable() {
      this.tableLoading = true
      this.tableError = ''
      try {
        const params = Object.assign({}, this.buildQueryParams(), {
          page: this.page.currentPage,
          size: this.page.pageSize
        })
        const data = this.unwrapResponse(await fetchPage(params)) || {}
        this.records = this.normalizeArray(data.records || data.list || data.rows || data)
        this.page.total = Number(data.total || data.totalRow || this.records.length) || 0
      } catch (e) {
        console.warn(e)
        this.records = []
        this.page.total = 0
        this.tableError = '明细数据加载失败，请稍后重试'
      } finally {
        this.tableLoading = false
      }
    },
    async loadCustomerOptions(keyword) {
      this.customerLoading = true
      try {
        const data = this.unwrapResponse(await fetchCustomerOptions(keyword ? { key: keyword } : {}))
        this.customerOptions = this.normalizeArray(data).map(item => ({
          id: item.id || item.deptId || item.value,
          deptName: item.deptName || item.name || item.label
        })).filter(item => item.id)
      } catch (e) {
        console.warn(e)
        this.customerOptions = []
      } finally {
        this.customerLoading = false
      }
    },
    async loadCargoOptions(keyword) {
      this.cargoLoading = true
      try {
        const data = this.unwrapResponse(await fetchCargoOptions(keyword ? { varName: keyword } : {}))
        this.cargoOptions = this.normalizeArray(data).map(item => ({
          varNo: item.varNo || item.value,
          varName: item.varName || item.name || item.label
        })).filter(item => item.varNo)
      } catch (e) {
        console.warn(e)
        this.cargoOptions = []
      } finally {
        this.cargoLoading = false
      }
    },
    buildQueryParams() {
      const timeoutHours = Number(this.queryForm.timeoutHours)
      const params = {
        businessType: 2,
        timeoutSeconds: Math.round((Number.isFinite(timeoutHours) && timeoutHours > 0 ? timeoutHours : DEFAULT_TIMEOUT_HOURS) * 3600)
      }
      Object.keys(this.queryForm).forEach(key => {
        if (key === 'timeoutHours') return
        const value = this.queryForm[key]
        if (value !== '' && value !== null && value !== undefined) {
          params[key] = value
        }
      })
      if (this.dateRange && this.dateRange.length === 2) {
        params.startTime = this.dateRange[0]
        params.endTime = this.dateRange[1]
      }
      return params
    },
    handleSearch() {
      if (!this.queryForm.timeoutHours || Number(this.queryForm.timeoutHours) <= 0) {
        this.$message.warning('超时阈值必须大于0')
        return
      }
      this.page.currentPage = 1
      this.loadData()
    },
    handleReset() {
      this.queryForm = this.defaultQueryForm()
      this.setQuickRange('seven', false)
      this.page.currentPage = 1
      this.loadData()
    },
    async handleExport() {
      if (this.queryLoading) {
        this.$message.warning('数据加载中，请稍后导出')
        return
      }
      const total = this.page.total || 0
      if (total < 1) {
        this.$message.warning('无数据可导出')
        return
      }
      if (total > EXPORT_MAX_ROWS) {
        this.$message.warning(`当前最多导出 ${EXPORT_MAX_ROWS} 条，请缩小筛选范围后重试`)
        return
      }
      this.exportLoading = true
      try {
        const res = await exportExcel(this.buildQueryParams())
        downloadXls(res.data || res, `异常车辆预警_${Date.now()}.xlsx`)
        this.$message.success('导出成功')
      } catch (e) {
        console.warn(e)
        this.$message.error((e && e.message) || '导出失败')
      } finally {
        this.exportLoading = false
      }
    },
    handleCurrentChange(currentPage) {
      this.page.currentPage = currentPage
      this.loadTable()
    },
    handleSizeChange(pageSize) {
      this.page.pageSize = pageSize
      this.page.currentPage = 1
      this.loadTable()
    },
    setQuickRange(type, reload = true) {
      this.activeRange = type
      const now = new Date()
      let start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6)
      if (type === 'today') {
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      } else if (type === 'month') {
        start = new Date(now.getFullYear(), now.getMonth(), 1)
      }
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      this.dateRange = [`${this.formatDate(start)} 00:00:00`, `${this.formatDate(end)} 23:59:59`]
      if (reload) this.handleSearch()
    },
    applySummary(data) {
      const summary = data.summary || data.kpi || data
      const timeoutCount = this.pick(summary, ['timeoutVehicleCount', 'timeoutCount', 'alarmCount', 'total'])
      const currentCount = this.pick(summary, ['currentTimeoutCount', 'currentInPlantTimeoutCount', 'runningTimeoutCount'])
      const finishedCount = this.pick(summary, ['finishedTimeoutCount', 'completedTimeoutCount', 'outgateTimeoutCount'])
      const avgOvertime = this.pick(summary, ['avgOvertimeSeconds', 'avgTimeoutSeconds'])
      const maxOvertime = this.pick(summary, ['maxOvertimeSeconds', 'maxTimeoutSeconds'])
      this.summaryCards = [
        {
          key: 'timeoutVehicleCount',
          label: '超时车辆数',
          caption: '全部',
          value: this.formatInteger(timeoutCount),
          unit: '车',
          sub: `阈值 ${this.formatDuration(this.buildQueryParams().timeoutSeconds)}`,
          accent: 'accent-red'
        },
        {
          key: 'currentTimeoutCount',
          label: '当前在厂超时',
          caption: '进行中',
          value: this.formatInteger(currentCount),
          unit: '车',
          sub: '未出门车辆按当前时间动态计算',
          accent: 'accent-orange'
        },
        {
          key: 'finishedTimeoutCount',
          label: '已出厂超时',
          caption: '已完成',
          value: this.formatInteger(finishedCount),
          unit: '车',
          sub: '已出门车辆按实际在厂时长计算',
          accent: 'accent-blue'
        },
        {
          key: 'avgOvertimeSeconds',
          label: '平均超时时长',
          caption: '均值',
          value: this.formatDuration(avgOvertime),
          unit: '',
          sub: '按超时时长统计',
          accent: 'accent-green'
        },
        {
          key: 'maxOvertimeSeconds',
          label: '最大超时时长',
          caption: '峰值',
          value: this.formatDuration(maxOvertime),
          unit: '',
          sub: '用于定位严重滞留车辆',
          accent: 'accent-cyan'
        }
      ]
      this.customerTop = this.normalizeRank(data.customerTop || data.customerRank || data.customerTop5)
      this.durationBuckets = this.normalizeRank(data.durationBuckets || data.durationDistribution || data.durationRange)
    },
    normalizeRank(list) {
      return this.normalizeArray(list).slice(0, 5).map(item => ({
        name: item.name || item.label || item.range || item.bucket || item.custName || item.customerName || item.cargoName || '--',
        value: this.pick(item, ['value', 'count', 'vehicleCount', 'timeoutCount', 'total'])
      }))
    },
    renderCharts() {
      this.renderCustomerChart()
      this.renderDurationChart()
    },
    renderCustomerChart() {
      const el = this.$refs.customerChart
      if (!el) return
      if (!this.charts.customer) this.charts.customer = echarts.init(el)
      const names = this.customerTop.map(item => item.name)
      const values = this.customerTop.map(item => Number(item.value) || 0)
      this.charts.customer.setOption({
        color: ['#f56c6c'],
        title: names.length ? null : this.emptyChartTitle('暂无客户超时数据'),
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 42, right: 18, top: 28, bottom: 42 },
        xAxis: {
          type: 'category',
          data: names,
          axisLabel: { color: '#606266', interval: 0, overflow: 'truncate', width: 82 },
          axisLine: { lineStyle: { color: '#dcdfe6' } }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          axisLabel: { color: '#606266' },
          splitLine: { lineStyle: { color: '#edf2f7' } }
        },
        series: [
          {
            name: '超时车辆数',
            type: 'bar',
            data: values,
            barMaxWidth: 30,
            itemStyle: { borderRadius: [3, 3, 0, 0] }
          }
        ]
      }, true)
    },
    renderDurationChart() {
      const el = this.$refs.durationChart
      if (!el) return
      if (!this.charts.duration) this.charts.duration = echarts.init(el)
      const rows = this.durationBuckets.map(item => ({
        name: item.name,
        value: Number(item.value) || 0
      }))
      this.charts.duration.setOption({
        color: ['#409eff', '#67c23a', '#e6a23c', '#f56c6c', '#909399'],
        title: rows.length ? null : this.emptyChartTitle('暂无时长分布数据'),
        tooltip: { trigger: 'item' },
        legend: { bottom: 4, type: 'scroll', textStyle: { color: '#606266' } },
        series: [
          {
            name: '车辆数',
            type: 'pie',
            radius: ['42%', '66%'],
            center: ['50%', '44%'],
            avoidLabelOverlap: true,
            label: { formatter: '{b}: {c}' },
            data: rows
          }
        ]
      }, true)
    },
    emptyChartTitle(text) {
      return {
        text,
        left: 'center',
        top: 'middle',
        textStyle: { color: '#909399', fontSize: 14, fontWeight: 400 }
      }
    },
    disposeCharts() {
      Object.keys(this.charts).forEach(key => {
        if (this.charts[key]) {
          this.charts[key].dispose()
          this.charts[key] = null
        }
      })
    },
    resizeCharts() {
      Object.keys(this.charts).forEach(key => {
        if (this.charts[key]) this.charts[key].resize()
      })
    },
    unwrapResponse(res) {
      const body = res && res.data !== undefined ? res.data : res
      if (body && body.data !== undefined) return body.data
      return body
    },
    normalizeArray(data) {
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.records)) return data.records
      if (data && Array.isArray(data.rows)) return data.rows
      if (data && Array.isArray(data.list)) return data.list
      if (data && Array.isArray(data.data)) return data.data
      return []
    },
    pick(target, keys) {
      if (!target) return undefined
      for (let i = 0; i < keys.length; i++) {
        const value = target[keys[i]]
        if (value !== undefined && value !== null && value !== '') return value
      }
      return undefined
    },
    vehicleStatusText(value) {
      const found = this.vehicleStatusOptions.find(item => String(item.value) === String(value))
      return found ? found.label : '--'
    },
    vehicleStatusTag(value) {
      if (String(value) === '1') return 'warning'
      if (String(value) === '2') return 'success'
      if (String(value) === '0') return 'info'
      return ''
    },
    formatInteger(value) {
      const num = Number(value)
      if (value === null || value === undefined || value === '' || Number.isNaN(num)) return '0'
      return Math.trunc(num).toLocaleString()
    },
    formatNumber(value) {
      const num = Number(value)
      if (value === null || value === undefined || value === '' || Number.isNaN(num)) return '--'
      return num.toLocaleString(undefined, { maximumFractionDigits: 2 })
    },
    formatDuration(seconds) {
      const num = Number(seconds)
      if (seconds === null || seconds === undefined || seconds === '' || Number.isNaN(num)) return '--'
      const abs = Math.max(0, Math.trunc(num))
      const h = Math.floor(abs / 3600)
      const m = Math.floor((abs % 3600) / 60)
      if (h > 0) return `${h}小时${m}分`
      return `${m}分`
    },
    formatDateTime(value) {
      if (value === null || value === undefined || value === '') return '--'
      const str = String(value).trim()
      const match = str.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})/)
      if (match) return `${match[1]} ${match[2]}`
      return str
    },
    formatDate(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }
  }
}
</script>

<style scoped lang="scss">
.vehicle-alarm-page {
  min-width: 1180px;
  padding: 12px;
  background: #eef3f8;
  color: #1f2937;
}

.filter-panel,
.panel,
.table-panel,
.metric-card {
  border: 1px solid #dfe7f1;
  border-radius: 6px;
  background: #fff;
  box-shadow: none;
}

.filter-panel {
  padding: 12px 14px;
  margin-bottom: 12px;
}

.filter-form {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 12px;
  align-items: center;

  ::v-deep .el-form-item {
    margin: 0;
  }

  ::v-deep .el-form-item:not(.filter-actions) {
    width: 236px;
  }

  ::v-deep .el-form-item:first-child {
    width: 390px;
  }

  ::v-deep .el-date-editor,
  ::v-deep .el-select,
  ::v-deep .el-input,
  ::v-deep .el-input-number {
    width: 100%;
  }
}

.filter-actions {
  ::v-deep .el-form-item__content {
    margin-left: 0 !important;
    white-space: nowrap;
  }
}

.quick-range {
  display: flex;
  gap: 6px;
  padding-top: 10px;
  margin-left: 76px;
}

.state-alert {
  margin-bottom: 12px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  min-height: 114px;
  margin-bottom: 12px;
}

.metric-card {
  min-width: 0;
  min-height: 88px;
  padding: 13px 14px;
  overflow: hidden;
}

.metric-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  color: #64748b;
  font-size: 12px;
  line-height: 18px;
}

.metric-head span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metric-value {
  margin-top: 10px;
  color: #111827;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.15;
  white-space: nowrap;
}

.metric-value small {
  margin-left: 4px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 400;
}

.metric-foot {
  margin-top: 7px;
  overflow: hidden;
  color: #94a3b8;
  font-size: 12px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.accent-red {
  border-top: 3px solid #f56c6c;
}

.accent-orange {
  border-top: 3px solid #e6a23c;
}

.accent-blue {
  border-top: 3px solid #409eff;
}

.accent-green {
  border-top: 3px solid #67c23a;
}

.accent-cyan {
  border-top: 3px solid #06b6d4;
}

.analysis-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.panel,
.table-panel {
  padding: 14px;
  overflow: hidden;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 43px;
  margin: -14px -14px 12px;
  padding: 0 14px;
  border-bottom: 1px solid #e5edf6;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  box-sizing: border-box;
}

.panel-head span:last-child {
  overflow: hidden;
  color: #8a97a8;
  font-size: 12px;
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chart-box {
  height: 260px;
}

.table-panel {
  margin-bottom: 12px;
}

.table-head {
  margin-bottom: 10px;
}

.table-error {
  margin-bottom: 10px;
}

.overtime-text {
  color: #f56c6c;
  font-weight: 600;
}

.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding-top: 10px;
  color: #6b7280;
}

::v-deep .el-table th.el-table__cell {
  padding: 0;
  background: #f6f8fb;
  color: #344054;
  font-weight: 600;
  border-right: none;
}

::v-deep .el-table td.el-table__cell {
  padding: 8px 0;
  border-right: none;
}

::v-deep .el-table::before,
::v-deep .el-table::after {
  display: none;
}

@media (max-width: 1360px) {
  .summary-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .analysis-grid {
    grid-template-columns: 1fr;
  }

  .filter-actions {
    ::v-deep .el-form-item__content {
      margin-left: 76px !important;
    }
  }
}
</style>
